import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FlatList,
  LayoutRectangle,
  StyleSheet,
  Text,
  View,
  ViewProps,
} from "react-native";
import { TimeZone } from "../../types";
import { isWithinInterval } from "date-fns";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { last } from "lodash";
import { config } from "../config";
import { AbsoluteView } from "./AbsoluteView";
import { LocationIcon } from "./Icons";
import { useDateFormatter } from "../hooks/useDateFormatter";
import { errorReporting } from "../helpers/errorReporting";
import { ScheduleEvent } from "../providers/ConfigProvider";
import { ThemeOptions, useTheme } from "../providers/ThemeProvider";
import { PageBullets } from "./PageBullets";
import { Spacer } from "./ViewComponents";
dayjs.extend(timezone);
dayjs.extend(utc);

const baseEventHeight = 74;

const makeLayoutStyles = (theme: ThemeOptions, eventHeight: number) => {
  return StyleSheet.create({
    itemWrapper: {
      flexDirection: "row",
      paddingHorizontal: theme.spacing.medium,
      borderLeftWidth: theme.borderWidth.large,
      borderColor: "transparent",
      overflow: "hidden",
      height: eventHeight,
    },
    itemWrapperActive: {
      backgroundColor: theme.toRgba(theme.colors.white, 0.15),
      borderLeftColor: theme.backgroundText,
    },
    timeWrapper: {
      alignItems: "flex-start",
      justifyContent: "center",
    },
    startTimeText: {
      color: theme.backgroundText,
      fontSize: theme.fontSize.medium,
    },
    endTimeText: {
      color: theme.backgroundText,
      fontSize: theme.fontSize.body,
      opacity: 0.6,
    },
    detailsWrapper: {
      justifyContent: "center",
      flexGrow: 1,
      maxWidth: "75%",
    },
    titleText: {
      color: theme.backgroundText,
      fontSize: theme.fontSize.medium,
    },
    wideRow: {
      flexDirection: "row",
      alignItems: "center",
      paddingHorizontal: theme.spacing.medium,
      borderLeftWidth: theme.borderWidth.large,
      borderColor: "transparent",
      height: eventHeight,
    },
    wideCell: {
      color: theme.backgroundText,
      fontSize: theme.fontSize.medium,
    },
    wideMuted: { opacity: 0.6 },
    wideTime: { width: theme.scaled(110) },
    wideTitle: { flex: 1, paddingRight: theme.spacing.medium },
    wideLocation: { textAlign: "right" },
    detailsIcon: {
      width: theme.iconSize.small,
      height: theme.iconSize.small,
    },
    locationWrapper: {
      marginTop: theme.spacing.xxsmall,
      flexDirection: "row",
      alignItems: "center",
    },
    locationText: {
      color: theme.backgroundText,
      fontSize: theme.fontSize.body,
      opacity: 0.6,
      maxWidth: "80%",
    },
    todayText: {
      color: theme.backgroundText,
      padding: theme.spacing.medium,
      fontSize: theme.fontSize.h4,
      borderLeftWidth: theme.borderWidth.large,
      borderColor: "transparent",
      opacity: 0.6,
    },
    tomorrowText: {
      color: theme.backgroundText,
      fontSize: theme.fontSize.medium,
    },
    tomorrowWrapper: {
      justifyContent: "center",
      alignItems: "center",
      borderTopWidth: theme.borderWidth.regular,
      borderBottomWidth: theme.borderWidth.regular,
      borderColor: theme.toRgba(theme.contrastColor, 0.2),
      borderStyle: "dashed",
      padding: theme.spacing.small,
    },
  });
};

export type PaginatedEventsProps = {
  events: ScheduleEvent[];
  onLoopComplete?: () => void;
  timeZone?: TimeZone;
  style?: ViewProps["style"];
  disableLoop?: boolean;
  nextDayEvents?: ScheduleEvent[];
  isVertical?: boolean;
  onPageChange?: (currentIndex: number, total: number) => void;
};

export type ScheduleEventPage = { events: ScheduleEvent[]; height: number };

export const PaginatedEvents = ({
  events,
  timeZone,
  style,
  onLoopComplete,
  disableLoop,
  nextDayEvents,
  isVertical,
  onPageChange,
}: PaginatedEventsProps) => {
  const { t } = useTranslation("TemplateScreen");
  const theme = useTheme();
  const eventHeight = theme.scaled(baseEventHeight);
  const styles = makeLayoutStyles(theme, eventHeight);
  const [dimensions, setDimensions] = useState<LayoutRectangle>();
  const [currentIndex, setCurrentPage] = useState(0);
  const listRef = useRef<FlatList<ScheduleEventPage>>(null);

  const sortEvents = (events: ScheduleEvent[]) => {
    return events.sort((a, b) => {
      if (a.allDay && b.allDay) {
        return 0;
      } else if (a.allDay) {
        return -1;
      } else if (b.allDay) {
        return 1;
      } else {
        return new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime();
      }
    });
  };

  const eventPages = useMemo<ScheduleEventPage[]>(() => {
    const pages: ScheduleEventPage[] = [{ events: [], height: 0 }];

    if (dimensions != null) {
      const sorted = sortEvents(events);

      sorted.forEach((event) => {
        const lastPage = last(pages)!;
        if (
          lastPage.height + eventHeight <
          dimensions.height - theme.spacing.medium
        ) {
          lastPage.events.push(event);
          lastPage.height += eventHeight;
        } else {
          pages.push({ events: [event], height: eventHeight });
        }
      });
    }

    return pages;
  }, [dimensions, events, eventHeight, theme.spacing.medium]);

  const filteredNextDayEvents = useMemo<ScheduleEvent[]>(() => {
    const events: ScheduleEvent[] = [];

    if (dimensions != null) {
      sortEvents(nextDayEvents ?? []).forEach((event) => {
        const lastPage = last(eventPages)!;
        if (
          lastPage.height + eventHeight <
          dimensions.height - theme.spacing.medium
        ) {
          events.push(event);
          lastPage.height += eventHeight;
        } else {
          return events;
        }
      });
    }

    return events;
  }, [
    dimensions,
    eventPages,
    nextDayEvents,
    eventHeight,
    theme.spacing.medium,
  ]);

  useEffect(() => {
    onPageChange?.(currentIndex, eventPages.length);
  }, [currentIndex, eventPages.length, onPageChange]);

  useEffect(() => {
    if (disableLoop) return;
    if (eventPages.length <= 1) return;

    const rotationMs = config.eventsPaginated.changePageRate;

    const tick = () => {
      const next = Math.floor(Date.now() / rotationMs) % eventPages.length;
      setCurrentPage((prev) => {
        if (prev === next) return prev;
        listRef.current?.scrollToIndex({ index: next, animated: false });
        if (next === 0) onLoopComplete?.();
        return next;
      });
    };

    tick();
    const msUntilNextTick = rotationMs - (Date.now() % rotationMs);
    let intervalId: ReturnType<typeof setInterval> | undefined;
    const timeoutId = setTimeout(() => {
      tick();
      intervalId = setInterval(tick, rotationMs);
    }, msUntilNextTick);

    return () => {
      clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [eventPages, onLoopComplete, disableLoop]);

  return (
    <AbsoluteView
      style={style}
      onLayout={(ev) => setDimensions(ev.nativeEvent.layout)}
    >
      <FlatList<ScheduleEventPage>
        data={eventPages}
        horizontal
        ref={listRef}
        showsHorizontalScrollIndicator={false}
        onScrollToIndexFailed={() => {
          errorReporting.sendMessage(
            "PaginatedEvents: onScrollToIndexFailed silently failed",
          );
        }}
        renderItem={({ item: page, index }) => (
          <View
            style={[{ width: dimensions?.width, height: dimensions?.height }]}
          >
            {page.events.map((event) => {
              return (
                <EventItem
                  event={event}
                  timeZone={timeZone}
                  key={event.id}
                  isVertical={isVertical}
                />
              );
            })}
            {index === eventPages.length - 1 &&
              filteredNextDayEvents.length > 0 && (
                <View>
                  <View style={styles.tomorrowWrapper}>
                    <Text style={styles.tomorrowText}>{t("Tomorrow")}</Text>
                  </View>
                  {filteredNextDayEvents.map((event) => {
                    return (
                      <View
                        style={[{ width: dimensions?.width }]}
                        key={event.id}
                      >
                        <EventItem
                          event={event}
                          timeZone={timeZone}
                          isVertical={isVertical}
                        />
                      </View>
                    );
                  })}
                </View>
              )}
          </View>
        )}
      />
      {!disableLoop && !isVertical && (
        <PageBullets total={eventPages.length} currentIndex={currentIndex} />
      )}
    </AbsoluteView>
  );
};

const EventItem = ({
  event,
  timeZone,
  isVertical,
}: {
  event: ScheduleEvent;
  timeZone?: TimeZone;
  isVertical?: boolean;
}) => {
  const { t } = useTranslation("TemplateScreen");
  const theme = useTheme();
  const eventHeight = theme.scaled(baseEventHeight);
  const styles = makeLayoutStyles(theme, eventHeight);
  const formatter = useDateFormatter();

  const isActive = event.allDay
    ? dayjs().tz(timeZone).date() == dayjs(event.startsAt).utc().date()
    : isWithinInterval(dayjs().toDate(), {
        start: dayjs(event.startsAt).toDate(),
        end: dayjs(event.endsAt).toDate(),
      });

  if (isVertical) {
    const showEnd =
      !event.allDay && !dayjs(event.startsAt).isSame(event.endsAt);
    return (
      <View
        key={event.id}
        style={[
          styles.wideRow,
          isActive ? styles.itemWrapperActive : undefined,
        ]}
      >
        <Text style={[styles.wideCell, styles.wideTime]} numberOfLines={1}>
          {event.allDay
            ? t("All day")
            : formatter.hoursMinutes(dayjs(event.startsAt).toDate(), timeZone)}
        </Text>
        <Text
          style={[styles.wideCell, styles.wideTime, styles.wideMuted]}
          numberOfLines={1}
        >
          {showEnd
            ? formatter.hoursMinutes(dayjs(event.endsAt).toDate(), timeZone)
            : ""}
        </Text>
        <Text style={[styles.wideCell, styles.wideTitle]} numberOfLines={1}>
          {event.title}
        </Text>
        <Text
          style={[styles.wideCell, styles.wideLocation, styles.wideMuted]}
          numberOfLines={1}
        >
          {event.location ?? "—"}
        </Text>
      </View>
    );
  }

  return (
    <View
      key={event.id}
      style={[
        styles.itemWrapper,
        isActive ? styles.itemWrapperActive : undefined,
      ]}
    >
      <View style={styles.timeWrapper}>
        {event.allDay ? (
          <Text style={styles.startTimeText} numberOfLines={1}>
            {t("All day")}
          </Text>
        ) : (
          <>
            <Text style={styles.startTimeText} numberOfLines={1}>
              {formatter.hoursMinutes(dayjs(event.startsAt).toDate(), timeZone)}
            </Text>
            {!dayjs(event.startsAt).isSame(event.endsAt) && (
              <>
                <Spacer size="xxsmall" />
                <Text style={styles.endTimeText} numberOfLines={1}>
                  {formatter.hoursMinutes(
                    dayjs(event.endsAt).toDate(),
                    timeZone,
                  )}
                </Text>
              </>
            )}
          </>
        )}
      </View>
      <Spacer size="regular" />
      <View style={styles.detailsWrapper}>
        <Text numberOfLines={1} style={styles.titleText}>
          {event.title}
        </Text>
        {event.location != null && (
          <View style={styles.locationWrapper}>
            <LocationIcon
              style={styles.detailsIcon}
              fill={theme.backgroundText}
            />
            <Spacer size="xsmall" />
            <Text numberOfLines={1} style={styles.locationText}>
              {event.location}
            </Text>
          </View>
        )}
      </View>
    </View>
  );
};
